type Locationish = { pathname: string; search?: string; hash?: string }

const LOGIN_PATH = '/login'

/**
 * Builds the `/login` path carrying where the user is, as `redirectTo`.
 *
 * Encoded twice on purpose: flex-core's SAML redeem (`CGI.unescape` in
 * `retrieve_redirect_url`) decodes the `redirect_url` one extra time on the SSO
 * round-trip, so a single encode comes back with its nested query decoded and a
 * multi-param / date-range target corrupted. Double-encoding leaves exactly one
 * layer for `parseLoginRedirectTarget` to peel.
 *
 * Dependency-free (no store/react imports) so the logout saga can use it without a
 * circular import via `lib/auth` -> store -> sagas.
 */
export const buildLoginRedirectPath = ({ pathname, search = '', hash = '' }: Locationish): string => {
  if (pathname === LOGIN_PATH) {
    return `${pathname}${search}`
  }

  const target = `${pathname}${search}${hash}`

  return target === '/'
    ? LOGIN_PATH
    : `${LOGIN_PATH}?redirectTo=${encodeURIComponent(encodeURIComponent(target))}`
}

/**
 * Reads the post-login return target. flex-acp uses `redirectTo`; One Host sets
 * `returnTo`. `URLSearchParams.get` peels one encoded layer; if another remains
 * (the no-SSO path, which skips flex-core's extra decode), peel it too. Resolved
 * against our origin so only a same-origin path+query is kept (no open redirect).
 */
export const parseLoginRedirectTarget = (search?: string): string | null => {
  const params = new URLSearchParams(search ?? '')
  let value = params.get('redirectTo') || params.get('returnTo')
  if (!value) {
    return null
  }

  if (/^%2[fF]/.test(value)) {
    try {
      value = decodeURIComponent(value)
    } catch {
      return null
    }
  }

  try {
    const { pathname, search: query, hash } = new URL(value, window.origin)
    return `${pathname}${query}${hash}`
  } catch {
    return null
  }
}
